// src/pages/Login.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { sendOtp, verifyOtp, getRiderProfile, createOrUpdateRider, auth, setNativeOtpCallbacks, getAuthErrorKey, warmRecaptcha } from '../services/firebase';
import { useNativePhoneAuth } from '../utils/platform';
import { useRider } from '../context/RiderContext';
import { FEATURES } from '../config/features';
import OtpInput from '../components/OtpInput';
import { extractOtpCode, listenForWebOtpWithRetry } from '../utils/otpAutofill';

const AUTH_TOAST = {
  refererBlocked: 'Sign-in blocked: add https://rider.mytownly.in/* to your Firebase Browser API key HTTP referrers in Google Cloud Console → Credentials.',
  appNotAuthorized: 'App not authorized for OTP. In Firebase Console → Authentication → Settings → Authorized domains, add rider.mytownly.in. For APK, verify SHA-256 under Project settings → MyTownly Rider (in.mytownly.rider).',
  invalidPhone: 'Invalid phone number. Enter a valid 10-digit number.',
  tooManyRequests: 'Too many tries. Wait a few minutes.',
  quotaExceeded: 'SMS quota exceeded. Try again later or contact support.',
  captchaFailed: 'Security check failed. Close and reopen the app, then try again.',
  timeout: 'OTP request timed out. Check your network and try again.',
  networkFailed: 'Network error. Check your internet connection.',
  codeExpired: 'OTP expired. Tap Resend OTP.',
  invalidOtp: 'Wrong OTP. Try again.',
  noOtpSession: 'No OTP session. Tap Send OTP again.',
  sendFailed: 'Failed to send OTP. Try again.',
};

const authToast = (err) => AUTH_TOAST[getAuthErrorKey(err)] || AUTH_TOAST.sendFailed;

const VEHICLE_TYPES = {
  rides: ['Bajaj Auto', 'TVS Auto', 'Piaggio Auto', 'Other Auto'],
  delivery: ['Bike', 'Bicycle', 'Scooter', 'Walking'],
  both: ['Bajaj Auto', 'TVS Auto', 'Bike', 'Scooter', 'Other'],
};

export default function Login() {
  const [step, setStep] = useState('phone'); // phone → otp → name → workType → vehicle
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [workType, setWorkType] = useState(''); // 'rides' | 'delivery' | 'both'
  const [vehicleType, setVehicleType] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const verifyingRef = useRef(false);
  const attemptedOtpRef = useRef('');
  const navigate = useNavigate();
  const { setRider } = useRider();

  const finishLoginAfterOtp = useCallback(async (user) => {
    const profile = await getRiderProfile(user.uid);
    if (!profile?.workType) {
      setRider(user);
      setStep('name');
    } else {
      setRider(user);
      navigate('/');
    }
  }, [navigate, setRider]);

  const completeOtpLogin = useCallback(async (code) => {
    const normalized = extractOtpCode(code);
    if (normalized.length < 6) {
      toast.error('Enter complete OTP');
      return;
    }
    if (verifyingRef.current) return;
    if (attemptedOtpRef.current === normalized) return;
    attemptedOtpRef.current = normalized;
    verifyingRef.current = true;
    setLoading(true);
    setOtp(normalized);
    try {
      const user = await verifyOtp(normalized);
      await finishLoginAfterOtp(user);
    } catch (e) {
      console.error(e);
      const key = getAuthErrorKey(e);
      if (key === 'codeExpired' || key === 'invalidOtp') {
        toast.error(AUTH_TOAST[key]);
        attemptedOtpRef.current = '';
        if (key === 'codeExpired') setOtp('');
      } else {
        toast.error(authToast(e));
        attemptedOtpRef.current = '';
      }
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  }, [finishLoginAfterOtp]);

  const handleOtpChange = (code) => {
    setOtp(code);
    if (code.length < 6) attemptedOtpRef.current = '';
  };

  useEffect(() => {
    if (step === 'phone' && !useNativePhoneAuth()) warmRecaptcha();
  }, [step]);

  useEffect(() => {
    if (!useNativePhoneAuth()) return undefined;
    setNativeOtpCallbacks({
      onAutoVerified: async () => {
        const user = auth.currentUser;
        if (!user) return;
        setLoading(true);
        try {
          toast.success('Verified automatically');
          await finishLoginAfterOtp(user);
        } finally {
          setLoading(false);
        }
      },
      onCodeReceived: (code) => {
        const digits = extractOtpCode(code);
        attemptedOtpRef.current = '';
        setOtp(digits);
        completeOtpLogin(digits);
      },
    });
    return () => setNativeOtpCallbacks({ onAutoVerified: null, onCodeReceived: null });
  }, [completeOtpLogin, finishLoginAfterOtp]);

  useEffect(() => {
    if (step !== 'otp') return undefined;
    const ac = new AbortController();
    return listenForWebOtpWithRetry((code) => {
      const digits = extractOtpCode(code);
      if (digits.length < 6) return;
      attemptedOtpRef.current = '';
      setOtp(digits);
      completeOtpLogin(digits);
    }, ac.signal);
  }, [step, completeOtpLogin]);

  // ── STEP PROGRESS ──────────────────────────
  const profileSteps = FEATURES.RIDES_ENABLED ? ['name', 'workType', 'vehicle'] : ['name', 'vehicle'];
  const currentProfileStep = profileSteps.indexOf(step);
  const showProgress = currentProfileStep >= 0;

  // ── OTP ────────────────────────────────────
  const handleSendOtp = async () => {
    if (phone.length < 10) { toast.error('Enter valid 10-digit number'); return; }
    setLoading(true);
    try {
      const sent = await sendOtp(`+91${phone}`);
      if (sent?.autoVerified && auth.currentUser) {
        toast.success('Verified automatically');
        await finishLoginAfterOtp(auth.currentUser);
        return;
      }
      setOtp('');
      attemptedOtpRef.current = '';
      toast.success('OTP sent! 📱');
      setStep('otp');
    } catch (e) {
      console.error(e);
      toast.error(authToast(e));
    } finally { setLoading(false); }
  };

  const goAfterName = () => {
    if (!name.trim()) { toast.error('Enter your name'); return; }
    if (!FEATURES.RIDES_ENABLED) {
      setWorkType('delivery');
      setStep('vehicle');
    } else {
      setStep('workType');
    }
  };
  const handleSaveProfile = async () => {
    if (!vehicleNumber.trim()) { toast.error('Enter vehicle number'); return; }
    setLoading(true);
    try {
      const user = auth.currentUser;
      await createOrUpdateRider(user.uid, {
        name: name.trim(),
        phone: `+91${phone}`,
        workType: FEATURES.RIDES_ENABLED ? workType : 'delivery',
        vehicle: vehicleType,
        vehicleNumber: vehicleNumber.trim().toUpperCase(),
        isApproved: false, // requires admin approval
        isBlocked: false,
        createdAt: new Date().toISOString(),
      });
      setRider(user);
      toast.success(`Welcome, ${name}! Waiting for approval 🎉`);
      navigate('/');
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(145deg, #111318, #0a0a0a)', padding: '40px 28px 32px', position: 'relative', overflow: 'hidden', borderBottom: '1px solid var(--border)' }}>
        <div style={{ position: 'absolute', right: -40, top: -40, width: 200, height: 200, borderRadius: '50%', background: 'var(--green-glow)' }} />
        <div style={{ fontSize: 44, marginBottom: 10 }}>{FEATURES.RIDES_ENABLED ? '🛺' : '📦'}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--green)' }}>MyTownly</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>
          {FEATURES.RIDES_ENABLED ? 'Rider & Delivery Partner App' : 'Grocery Delivery Partner App'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>Deliver groceries in Piler, AP</div>

        {/* Progress bar for profile steps */}
        {showProgress && (
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--gray)', fontFamily: 'DM Mono, monospace' }}>
                STEP {currentProfileStep + 1} OF {profileSteps.length}
              </span>
              <span style={{ fontSize: 11, color: 'var(--green)', fontFamily: 'DM Mono, monospace' }}>
                {step === 'name' ? 'YOUR NAME' : step === 'workType' ? 'WORK TYPE' : 'VEHICLE'}
              </span>
            </div>
            <div style={{ height: 3, background: 'var(--border)', borderRadius: 2 }}>
              <div style={{ height: '100%', background: 'var(--green)', borderRadius: 2, width: `${((currentProfileStep + 1) / profileSteps.length) * 100}%`, transition: 'width 0.4s ease' }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '32px 24px', flex: 1 }}>

        {/* ── STEP: PHONE ── */}
        {step === 'phone' && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Enter your number</div>
            <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 24 }}>We'll verify your identity via OTP</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <div style={{ background: 'var(--card)', borderRadius: 12, padding: '14px 12px', fontSize: 14, fontWeight: 700, border: '1.5px solid var(--border)', flexShrink: 0, color: 'var(--text)' }}>🇮🇳 +91</div>
              <input type="tel" placeholder="10-digit number" value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                style={{ flex: 1, padding: 14, fontSize: 18, fontWeight: 700, letterSpacing: 3 }} />
            </div>
            <button className="btn btn-green" onClick={handleSendOtp} disabled={loading || phone.length < 10}>
              {loading ? <span className="spinner" /> : 'Send OTP →'}
            </button>
          </div>
        )}

        {/* ── STEP: OTP ── */}
        {step === 'otp' && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Enter OTP</div>
            <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 24 }}>
              Sent to +91 {phone} <span style={{ color: 'var(--green)', cursor: 'pointer' }} onClick={() => setStep('phone')}>Change</span>
            </div>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              onComplete={completeOtpLogin}
              disabled={loading}
              autoFocus
            />
            <button className="btn btn-green" onClick={() => completeOtpLogin(otp)} disabled={loading || otp.length < 6}>
              {loading ? <span className="spinner" /> : 'Verify →'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--gray)' }}>
              Didn't get it? <span style={{ color: 'var(--green)', cursor: 'pointer' }} onClick={handleSendOtp}>Resend</span>
            </div>
          </div>
        )}

        {/* ── STEP 1: NAME ── */}
        {step === 'name' && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>What's your name? 👋</div>
            <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 28 }}>This will be shown to customers</div>
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18 }}>👤</span>
              <input
                type="text" placeholder="Your full name"
                value={name} onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && name.trim() && goAfterName()}
                style={{ paddingLeft: 44, fontSize: 16, fontWeight: 600 }}
                autoFocus
              />
            </div>
            <button className="btn btn-green" onClick={goAfterName} disabled={!name.trim()}>
              Next →
            </button>
          </div>
        )}

        {/* ── STEP 2: WORK TYPE ── */}
        {FEATURES.RIDES_ENABLED && step === 'workType' && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>What do you do? 💼</div>
            <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 28 }}>Choose what you want to offer in Piler</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
              {[
                { id: 'rides', emoji: '🛺', title: 'Auto Rides', desc: 'Pick up and drop customers around Piler' },
                { id: 'delivery', emoji: '📦', title: 'Grocery Delivery', desc: 'Deliver grocery orders to customers' },
                { id: 'both', emoji: '🔄', title: 'Both', desc: 'Do rides and deliveries' },
              ].map(w => (
                <div key={w.id} onClick={() => setWorkType(w.id)}
                  style={{ padding: 16, borderRadius: 16, border: `2px solid ${workType === w.id ? 'var(--green)' : 'var(--border)'}`, background: workType === w.id ? 'rgba(0,214,143,0.06)' : 'var(--card)', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: workType === w.id ? 'var(--green)' : 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0, transition: 'all 0.2s' }}>
                    {w.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: workType === w.id ? 'var(--green)' : 'var(--text)' }}>{w.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>{w.desc}</div>
                  </div>
                  {workType === w.id && (
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>✓</div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => setStep('name')}>← Back</button>
              <button className="btn btn-green" style={{ flex: 2 }} onClick={() => { if (!workType) { toast.error('Select what you do'); return; } setStep('vehicle'); }} disabled={!workType}>
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: VEHICLE ── */}
        {step === 'vehicle' && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Your vehicle 📦</div>
            <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 28 }}>Grocery delivery in Piler</div>

            {/* Vehicle type selector */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 10, letterSpacing: 0.5 }}>VEHICLE TYPE</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {VEHICLE_TYPES[FEATURES.RIDES_ENABLED ? workType : 'delivery']?.map(v => (
                  <div key={v} onClick={() => setVehicleType(v)}
                    style={{ padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${vehicleType === v ? 'var(--green)' : 'var(--border)'}`, background: vehicleType === v ? 'rgba(0,214,143,0.08)' : 'var(--card)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: vehicleType === v ? 'var(--green)' : 'var(--text)', transition: 'all 0.15s' }}>
                    {v}
                  </div>
                ))}
              </div>
            </div>

            {/* Vehicle number */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 8, letterSpacing: 0.5 }}>VEHICLE NUMBER PLATE</div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>🔢</span>
                <input
                  type="text" placeholder="e.g. AP39AB1234"
                  value={vehicleNumber}
                  onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
                  style={{ paddingLeft: 44, fontSize: 15, fontWeight: 700, letterSpacing: 2 }}
                />
              </div>
            </div>

            {/* Summary */}
            {name && workType && vehicleType && vehicleNumber && (
              <div style={{ marginBottom: 20, padding: 14, background: 'rgba(0,214,143,0.05)', borderRadius: 14, border: '1px solid rgba(0,214,143,0.2)' }}>
                <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 700, marginBottom: 8 }}>📋 Profile Summary</div>
                {[
                  { label: 'Name', value: name },
                  { label: 'Role', value: workType === 'rides' ? '🛺 Auto Rides' : workType === 'delivery' ? '📦 Delivery' : '🔄 Rides + Delivery' },
                  { label: 'Vehicle', value: vehicleType },
                  { label: 'Plate', value: vehicleNumber },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderBottom: '1px solid rgba(0,214,143,0.1)' }}>
                    <span style={{ color: 'var(--gray)' }}>{r.label}</span>
                    <span style={{ fontWeight: 700 }}>{r.value}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => setStep(FEATURES.RIDES_ENABLED ? 'workType' : 'name')}>← Back</button>
              <button className="btn btn-green" style={{ flex: 2 }} onClick={handleSaveProfile} disabled={loading || !vehicleType || !vehicleNumber.trim()}>
                {loading ? <span className="spinner" /> : 'Submit for Approval 🎉'}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: 'var(--gray)', lineHeight: 1.6 }}>
              Your profile will be reviewed by the MyTownly team.<br />You'll be approved shortly to start delivering!
            </div>
          </div>
        )}

      </div>

      <div style={{ padding: '16px 24px', textAlign: 'center', fontSize: 11, color: 'var(--gray)' }}>
        MyTownly {FEATURES.RIDES_ENABLED ? 'Rider' : 'Delivery'} App • Piler, AP
      </div>
      <div id="recaptcha-anchor" aria-hidden="true" />
    </div>
  );
}